import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollToTop } from '../hooks/useScrollToTop';
import { formatPhoneNumber } from '../utils';
import toast from 'react-hot-toast';
import Loading from '../components/Loading';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoading } = useAuth();
  const { t } = useLanguage();

  // 페이지 마운트 시 상단으로 스크롤
  useScrollToTop();

  const [formData, setFormData] = useState({
    loginId: '',
    password: '',
  });

  // 로그인 시도 경고 메시지
  const [loginWarning, setLoginWarning] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // loginId가 숫자로만 이루어진 경우 전화번호 포맷팅 적용
    if (name === 'loginId') {
      const numbersOnly = value.replace(/[^0-9]/g, '');
      // 숫자로만 이루어진 경우 (또는 하이픈 포함) 전화번호로 간주
      if (numbersOnly.length > 0 && value.replace(/-/g, '') === numbersOnly) {
        setFormData({
          ...formData,
          [name]: formatPhoneNumber(value),
        });
        return;
      }
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginWarning(null);

    try {
      const response = await login(formData);

      if (response.success && response.data) {
        const { userType } = response.data;

        // 사용자 타입에 따라 리다이렉트
        if (userType === 'teacher') {
          navigate('/admin/dashboard');
        } else {
          navigate('/user/dashboard');
        }
      } else {
        toast.error(response.message || '로그인에 실패했습니다.');
      }
    } catch (error) {
      const err = error as { response?: { data?: { message?: string; warning?: string; locked?: boolean } } };
      const errorData = err.response?.data;

      // 경고 메시지 표시 (2-4회 실패 시)
      if (errorData?.warning) {
        setLoginWarning(errorData.warning);
      }

      // 잠금 상태 (5회 실패)
      if (errorData?.locked) {
        setLoginWarning(errorData.message || '로그인 시도 횟수를 초과했습니다.');
      }

      toast.error(errorData?.message || '로그인 중 오류가 발생했습니다.');
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <Link to="/" className="login-title-link">
            <h1 className="login-title">DREAM<span className="logo-accent">DRUMS</span></h1>
          </Link>
          <p className="login-subtitle">{t('login_subtitle')}</p>
        </div>

        <div className="form-container">

            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="loginId" className="form-label">
                  {t('username')} / {t('phone')}
                </label>
                <input
                  type="text"
                  id="loginId"
                  name="loginId"
                  value={formData.loginId}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="아이디 또는 전화번호를 입력하세요"
                  autoComplete="username"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  {t('password_login')}
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="비밀번호를 입력하세요"
                  autoComplete="current-password"
                  required
                />
              </div>

              {/* 로그인 시도 경고 메시지 */}
              {loginWarning && (
                <div style={{
                  color: '#dc2626',
                  fontSize: '14px',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  {loginWarning}
                </div>
              )}

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary btn-md"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Loading size="small" message="" />
                      {t('logging_in')}
                    </div>
                  ) : (
                    t('login_button')
                  )}
                </button>
              </div>

              <div className="login-footer">
                <div className="footer-links">
                  <Link to="/" className="footer-link">홈</Link>
                  <span className="footer-separator">|</span>
                  <Link to="/forgot-password" className="footer-link">비밀번호 재설정</Link>
                  <span className="footer-separator">|</span>
                  <Link to="/contact" className="footer-link">문의하기</Link>
                </div>
              </div>
            </form>
        </div>
      </div>
    </div>
  );
};

export default Login; 